// src/pages/shop/ShopAuditLog.jsx
//
// Журнал действий магазина. Только для владельца.
// Лента однострочных событий с раскрытием деталей по тапу.
// Фильтры: период / сотрудник / тип действия. Подгрузка по nextCursor.

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { audit as auditApi } from '../../api';
import { useAuth } from '../../hooks/useAuth';
import Icon from '../../components/Icon';

const PAGE_SIZE = 50;

// Период → начальная дата (ISO) или null для «всё время»
const PERIODS = ['all', 'today', 'week', 'month'];
function periodToFrom(period) {
  const now = new Date();
  if (period === 'today') {
    const d = new Date(now);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }
  if (period === 'week')  return new Date(now.getTime() - 7  * 86400000).toISOString();
  if (period === 'month') return new Date(now.getTime() - 30 * 86400000).toISOString();
  return null; // all
}

// Категории действий для фильтра (через actionPrefix на бэке)
const ACTION_GROUPS = [
  { key: '',              prefix: '' },
  { key: 'product',       prefix: 'product' },
  { key: 'variant',       prefix: 'variant' },
  { key: 'sale',          prefix: 'sale' },
  { key: 'stock',         prefix: 'stock' },
  { key: 'wholesale',     prefix: 'wholesale' },
  { key: 'cafe',          prefix: 'cafe' },
  { key: 'counterparty',  prefix: 'counterparty' },
  { key: 'employee',      prefix: 'employee' },
  { key: 'category',      prefix: 'category' },
  { key: 'shop',          prefix: 'shop' },
];

// Иконка по типу сущности/действия
function iconForAction(action = '') {
  const base = action.split('.')[0];
  switch (base) {
    case 'product':
    case 'variant':      return 'package';
    case 'sale':         return 'cashier';
    case 'stock':        return 'refresh';
    case 'wholesale':    return 'fileText';
    case 'counterparty': return 'truck';
    case 'cafe':         return 'cashier';
    case 'employee':     return 'users';
    case 'category':     return 'folder';
    case 'shop':         return 'settings';
    default:             return 'list';
  }
}

const KNOWN_META_LABELS = {
  total: 'metaTotal',
  amount: 'metaAmount',
  qty: 'metaQty',
  money: 'metaMoney',
  pointsEarned: 'metaPoints',
  pointsRedeemed: 'metaPointsRedeemed',
  cashAmount: 'metaCash',
  number: 'metaNumber',
  itemCount: 'metaItemCount',
  weightItems: 'metaWeightItems',
  weightNames: 'metaWeightNames',
  direction: 'metaDirection',
  method: 'metaMethod',
  role: 'metaRole',
};

export default function ShopAuditLog() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isOwner } = useAuth();

  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [actors, setActors] = useState([]);
  const [period, setPeriod] = useState('all');
  const [actorId, setActorId] = useState('');
  const [actionPrefix, setActionPrefix] = useState('');
  const [expanded, setExpanded] = useState(null);

  // Загрузка сотрудников для фильтра — один раз
  useEffect(() => {
    auditApi.actors().then(r => setActors(Array.isArray(r) ? r : [])).catch(() => {});
  }, []);

  // Относительное время через Intl (локализованное)
  const rtf = new Intl.RelativeTimeFormat(i18n.language || 'ru', { numeric: 'auto' });
  function relTime(iso) {
    const then = new Date(iso).getTime();
    const diff = then - Date.now();
    const abs = Math.abs(diff);
    const min = 60000, hour = 3600000, day = 86400000;
    if (abs < hour)  return rtf.format(Math.round(diff / min),  'minute');
    if (abs < day)   return rtf.format(Math.round(diff / hour), 'hour');
    if (abs < 7 * day) return rtf.format(Math.round(diff / day), 'day');
    return new Date(iso).toLocaleDateString(i18n.language || undefined, { day: 'numeric', month: 'short' });
  }

  const buildParams = useCallback((cur) => {
    const from = periodToFrom(period);
    return {
      limit: PAGE_SIZE,
      ...(from ? { from } : {}),
      ...(actorId ? { actorId } : {}),
      ...(actionPrefix ? { actionPrefix } : {}),
      ...(cur ? { cursor: cur } : {}),
    };
  }, [period, actorId, actionPrefix]);

  // Перезагрузка при смене фильтров
  const reload = useCallback(() => {
    setLoading(true);
    setExpanded(null);
    auditApi.list(buildParams(null))
      .then(r => {
        setItems(r.items || []);
        setNextCursor(r.nextCursor || null);
        setCursor(null);
      })
      .catch(() => { setItems([]); setNextCursor(null); })
      .finally(() => setLoading(false));
  }, [buildParams]);

  useEffect(() => { reload(); }, [reload]);

  const loadMore = () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    auditApi.list(buildParams(nextCursor))
      .then(r => {
        setItems(prev => [...prev, ...(r.items || [])]);
        setNextCursor(r.nextCursor || null);
        setCursor(nextCursor);
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  // Текст действия: actorName + глагол + «label»
  function actionVerb(action) {
    const key = `audit.action.${String(action || '').replace(/\./g, '_')}`;
    const fallback = t('audit.action.unknown');
    const v = t(key);
    return v === key ? fallback : v;
  }

  function actorLabel(row) {
    if (row.actorType === 'owner')    return t('audit.actorOwner');
    if (row.actorType === 'customer') return t('audit.actorCustomer');
    return row.actorName || t('audit.actorEmployee');
  }

  // Не владелец — журнал недоступен
  if (!isOwner) {
    return (
      <div className="page" style={{ padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <button className="back-btn" onClick={() => navigate(-1)}>
            <Icon name="chevronLeft" size={18} />
          </button>
          <h1 style={{ fontSize: 22, fontWeight: 800, flex: 1 }}>{t('audit.title')}</h1>
        </div>
        <div style={{ textAlign: 'center', padding: 32, color: 'var(--fg-muted)' }}>
          <Icon name="lock" size={36} style={{ opacity: 0.3, marginBottom: 8 }} />
          <p style={{ fontSize: 13 }}>{t('audit.ownerOnly')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page" style={{ padding: 20 }}>
      {/* Шапка */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <button className="back-btn" onClick={() => navigate(-1)}>
          <Icon name="chevronLeft" size={18} />
        </button>
        <h1 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', flex: 1 }}>
          {t('audit.title')}
        </h1>
      </div>
      <p style={{ fontSize: 13, color: 'var(--fg-muted)', margin: '0 0 16px 44px' }}>
        {t('audit.subtitle')}
      </p>

      {/* Период */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, overflowX: 'auto', paddingBottom: 4 }}>
        {PERIODS.map(p => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            style={pill(period === p)}
          >
            {t(`audit.period.${p}`)}
          </button>
        ))}
      </div>

      {/* Сотрудник + тип действия */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <select
          value={actorId}
          onChange={e => setActorId(e.target.value)}
          style={selectStyle}
        >
          <option value="">{t('audit.actorAll')}</option>
          {[...actors]
            .sort((a, b) =>
              (a.active === false) === (b.active === false)
                ? String(a.name || '').localeCompare(String(b.name || ''))
                : (a.active === false ? 1 : -1)
            )
            .map(a => (
              <option key={a.id} value={a.id}>
                {a.active === false ? `${a.name} (${t('audit.deletedMark')})` : a.name}
              </option>
            ))}
        </select>
        <select
          value={actionPrefix}
          onChange={e => setActionPrefix(e.target.value)}
          style={selectStyle}
        >
          {ACTION_GROUPS.map(g => (
            <option key={g.key || 'all'} value={g.prefix}>
              {g.key ? t(`audit.group.${g.key}`) : t('audit.filterAll')}
            </option>
          ))}
        </select>
      </div>

      {loading && <div className="loader" style={{ padding: 24 }}><div className="spinner" /></div>}

      {!loading && items.length === 0 && (
        <div style={{ textAlign: 'center', padding: 32, color: 'var(--fg-muted)' }}>
          <Icon name="list" size={36} style={{ opacity: 0.3, marginBottom: 8 }} />
          <p style={{ fontSize: 13 }}>{t('audit.empty')}</p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {items.map((row, idx) => {
            const isDelete = /\.(delete|cancel|payment_delete|delete_pin_remove)$/.test(row.action || '');
            const open = expanded === row.id;
            return (
              <div key={row.id} style={{ borderTop: idx === 0 ? 'none' : '1px solid var(--border)' }}>
                <button
                  onClick={() => setExpanded(open ? null : row.id)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 14px', background: 'none', border: 'none',
                    cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <div style={{
                    width: 34, height: 34, borderRadius: 'var(--r-sm)', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: isDelete ? 'var(--danger-soft, rgba(220,38,38,0.1))' : 'var(--bg-subtle)',
                    color: isDelete ? 'var(--danger)' : 'var(--fg-muted)',
                  }}>
                    <Icon name={iconForAction(row.action)} size={17} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, lineHeight: 1.35, color: 'var(--fg)' }}>
                      <b style={{ fontWeight: 700 }}>{actorLabel(row)}</b>
                      {' '}{actionVerb(row.action)}
                      {row.entityLabel ? <> «{row.entityLabel}»</> : null}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--fg-faint)', marginTop: 2 }}>
                      {relTime(row.createdAt)}
                    </div>
                  </div>
                  <Icon name={open ? 'chevronDown' : 'chevronRight'} size={15} color="var(--fg-faint)" />
                </button>

                {open && (
                  <div style={{
                    padding: '0 14px 14px 60px', fontSize: 12.5, color: 'var(--fg-muted)',
                    display: 'flex', flexDirection: 'column', gap: 4,
                  }}>
                    <div>
                      <span style={{ color: 'var(--fg-faint)' }}>{t('audit.when')}: </span>
                      {new Date(row.createdAt).toLocaleString(i18n.language || undefined)}
                    </div>
                    {renderMeta(row.meta, t)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!loading && nextCursor && (
        <button
          onClick={loadMore}
          disabled={loadingMore}
          className="btn btn-secondary"
          style={{ width: '100%', marginTop: 12 }}
        >
          {loadingMore ? '…' : t('audit.loadMore')}
        </button>
      )}

      <div style={{ height: 20 }} />
    </div>
  );
}

// Раскрытие meta в человекочитаемые строки
function renderMeta(meta, t) {
  if (!meta || typeof meta !== 'object') return null;
  const rows = [];
  const SKIP = ['fields', 'changes', 'permissionsChanged', 'roleChanged', 'passwordChanged'];

  const fieldLabel = (f) => {
    const key = `audit.field.${f}`;
    const v = t(key);
    return v === key ? f : v;
  };
  const fmtVal = (v) => {
    if (v === null || v === undefined || v === '') return t('audit.valEmpty');
    if (v === true) return t('audit.valYes');
    if (v === false) return t('audit.valNo');
    return String(v);
  };

  // Реальные изменения «было → стало»
  if (meta.changes && typeof meta.changes === 'object') {
    for (const [field, ch] of Object.entries(meta.changes)) {
      if (!ch || typeof ch !== 'object') continue;
      rows.push(
        <div key={'ch_' + field}>
          <span style={{ color: 'var(--fg-faint)' }}>{fieldLabel(field)}: </span>
          <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>{fmtVal(ch.from)}</span>
          {' → '}
          <b>{fmtVal(ch.to)}</b>
        </div>
      );
    }
  }

  // Старый формат записей (до обновления) — список полей
  if (Array.isArray(meta.fields) && meta.fields.length > 0) {
    rows.push(
      <div key="fields">
        <span style={{ color: 'var(--fg-faint)' }}>{t('audit.fieldsChanged')}: </span>
        {meta.fields.map(fieldLabel).join(', ')}
      </div>
    );
  }

  // Флаги по сотруднику
  if (meta.permissionsChanged) rows.push(<div key="perm">• {t('audit.permissionsChanged')}</div>);
  if (meta.passwordChanged)    rows.push(<div key="pwd">• {t('audit.passwordChanged')}</div>);

  // Прочие скаляры (суммы, кол-во и т.п.)
  for (const [k, v] of Object.entries(meta)) {
    if (SKIP.includes(k)) continue;
    if (v === null || v === undefined || typeof v === 'object') continue;
    if (typeof v === 'boolean' && v === false) continue;
    const labelKey = KNOWN_META_LABELS[k];
    const label = labelKey ? t(`audit.${labelKey}`) : fieldLabel(k);
    rows.push(
      <div key={k}>
        <span style={{ color: 'var(--fg-faint)' }}>{label}: </span>
        {fmtVal(v)}
      </div>
    );
  }
  return rows;
}

function pill(active) {
  return {
    padding: '6px 14px', fontSize: 13, borderRadius: 999, whiteSpace: 'nowrap',
    background: active ? 'var(--brand-primary)' : 'var(--surface)',
    color: active ? '#fff' : 'var(--fg)',
    border: '1px solid ' + (active ? 'var(--brand-primary)' : 'var(--border)'),
    cursor: 'pointer', flexShrink: 0,
  };
}

const selectStyle = {
  flex: 1, minWidth: 0, padding: '8px 10px', fontSize: 13,
  borderRadius: 'var(--r-sm)', border: '1px solid var(--border)',
  background: 'var(--surface)', color: 'var(--fg)', cursor: 'pointer',
};
